import { useState } from 'react';
import type { User, FamilyRole } from '@kinapp/shared';
import { getDependentColor } from './DependentBadge';

export interface AttendeeState {
  selectedUserIds: string[];
  selectedDepIds: string[];
  externalGuests: Array<{ name: string; email?: string }>;
}

interface AttendeeSelectProps {
  dependents: Array<{ id: string; name: string }>;
  familyMembers: Array<{ user: User; role: FamilyRole }>;
  value: AttendeeState;
  onChange: (state: AttendeeState) => void;
}

export function AttendeeSelect({ dependents, familyMembers, value, onChange }: AttendeeSelectProps) {
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  const hasBothLists = familyMembers.length > 0 && dependents.length > 0;

  const toggleUser = (userId: string) => {
    const next = value.selectedUserIds.includes(userId)
      ? value.selectedUserIds.filter((id) => id !== userId)
      : [...value.selectedUserIds, userId];
    onChange({ ...value, selectedUserIds: next });
  };

  const toggleDep = (depId: string) => {
    const next = value.selectedDepIds.includes(depId)
      ? value.selectedDepIds.filter((id) => id !== depId)
      : [...value.selectedDepIds, depId];
    onChange({ ...value, selectedDepIds: next });
  };

  const removeGuest = (index: number) => {
    onChange({ ...value, externalGuests: value.externalGuests.filter((_, i) => i !== index) });
  };

  const addGuest = () => {
    const name = guestName.trim();
    if (!name) return;
    onChange({
      ...value,
      externalGuests: [...value.externalGuests, { name, email: guestEmail.trim() || undefined }],
    });
    setGuestName('');
    setGuestEmail('');
    setShowGuestForm(false);
  };

  return (
    <div className="mb-6">
      <label className="label mb-2">Attendees</label>

      {/* Circle members */}
      {familyMembers.length > 0 && (
        <div className="mb-3">
          {hasBothLists && (
            <p className="text-xs text-gray-500 mb-1.5">Circle members</p>
          )}
          <div className="flex flex-wrap gap-2">
            {familyMembers.map(({ user }) => {
              const selected = value.selectedUserIds.includes(user.id);
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => toggleUser(user.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    selected
                      ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${selected ? 'bg-indigo-500' : 'bg-gray-300'}`} />
                  {user.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Dependents */}
      {dependents.length > 0 && (
        <div className="mb-3">
          {hasBothLists && (
            <p className="text-xs text-gray-500 mb-1.5">Who it's for</p>
          )}
          <div className="flex flex-wrap gap-2">
            {dependents.map((dep, i) => {
              const color = getDependentColor(i);
              const selected = value.selectedDepIds.includes(dep.id);
              return (
                <button
                  key={dep.id}
                  type="button"
                  onClick={() => toggleDep(dep.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    selected
                      ? `${color.bg} ${color.text} ${color.border}`
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${selected ? color.dot : 'bg-gray-300'}`} />
                  {dep.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* External guests */}
      {value.externalGuests.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1.5">External guests</p>
          <div className="flex flex-wrap gap-2">
            {value.externalGuests.map((guest, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200"
              >
                {guest.name}
                {guest.email && <span className="text-gray-400 text-xs">({guest.email})</span>}
                <button
                  type="button"
                  onClick={() => removeGuest(i)}
                  className="ml-0.5 text-gray-400 hover:text-gray-600"
                  aria-label={`Remove ${guest.name}`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add external guest */}
      {showGuestForm ? (
        <div className="flex gap-2 items-end mt-2">
          <div className="flex-1">
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="input text-sm"
              placeholder="Name"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }}
            />
          </div>
          <div className="flex-1">
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              className="input text-sm"
              placeholder="Email (optional)"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }}
            />
          </div>
          <button
            type="button"
            onClick={addGuest}
            disabled={!guestName.trim()}
            className="btn btn-primary text-sm px-3 py-2 disabled:opacity-40"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => { setShowGuestForm(false); setGuestName(''); setGuestEmail(''); }}
            className="btn btn-secondary text-sm px-3 py-2"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowGuestForm(true)}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mt-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add external guest
        </button>
      )}
    </div>
  );
}
